import React, { useState } from "react";
import { TrendingUp, Layers, Shield, Award, Globe, ChevronRight } from "lucide-react";
import ScheduleCTA from "@/components/landing/ScheduleCTA";

/**
 * Interactive explanation of Indexed Funds vs ETFs.
 * - Toggle between two products
 * - Animated visualization of diversification
 * - Side-by-side comparison
 */

const PRODUCTS = {
  index: {
    key: "index",
    title: "Fondos Indexados",
    tagline: "Inversión diversificada y disciplinada",
    description:
      "Un fondo indexado es un instrumento que replica el comportamiento de un índice bursátil completo (por ejemplo, el S&P 500 o el MSCI World). En lugar de elegir acciones individuales, inviertes en cientos de empresas a la vez — con bajos costos y rendimientos alineados al mercado en el largo plazo.",
    keyPoints: [
      "Diversificación automática en cientos de empresas",
      "Comisiones significativamente menores que fondos activos",
      "Estrategia pasiva: replica el índice, no intenta superarlo",
      "Ideal para horizontes de mediano y largo plazo",
    ],
    example: "Ejemplo: invertir en un fondo S&P 500 equivale a tener una pequeña parte de las 500 empresas más grandes de EE. UU. — Apple, Microsoft, Amazon, Google, entre otras.",
  },
  etf: {
    key: "etf",
    title: "ETFs",
    tagline: "Exchange Traded Funds — la flexibilidad del mercado",
    description:
      "Un ETF (Exchange Traded Fund) es similar a un fondo indexado, pero cotiza en bolsa como una acción. Esto significa que puedes comprarlo o venderlo en cualquier momento del día a precio de mercado, con la diversificación de un fondo y la liquidez de una acción.",
    keyPoints: [
      "Se compran y venden en tiempo real durante el horario bursátil",
      "Existen ETFs de índices, sectores, países, materias primas, bonos",
      "Comisiones competitivas con respaldo institucional",
      "Transparencia diaria de holdings",
    ],
    example: "Ejemplo: con un ETF como VOO o IVV obtienes exposición al S&P 500 con el dinamismo de comprar y vender al instante.",
  },
};

const IndexedFunds = () => {
  const [active, setActive] = useState("index");
  const product = PRODUCTS[active];

  return (
    <section
      id="instrumentos"
      data-testid="indexed-funds-section"
      className="relative bg-[#0B0410] text-[#F9F6F0] py-24 lg:py-32 border-t border-white/5 overflow-hidden"
    >
      {/* Decorative hex pattern */}
      <div className="absolute inset-0 opacity-[0.25] pointer-events-none">
        <svg width="100%" height="100%" aria-hidden="true">
          <defs>
            <pattern
              id="hexInst"
              width="80"
              height="138"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M40 0 L80 23 L80 69 L40 92 L0 69 L0 23 Z"
                fill="none"
                stroke="#E6D5B8"
                strokeWidth="0.8"
                opacity="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hexInst)" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#E6D5B8]" />
              <span
                className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium"
                data-testid="instruments-eyebrow"
              >
                Instrumentos de inversión
              </span>
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08]"
              data-testid="instruments-headline"
            >
              ¿Qué son los Fondos Indexados{" "}
              <span className="italic text-[#E6D5B8]">y los ETFs</span>?
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base lg:text-lg text-[#F9F6F0]/70 font-light">
              Dos de los instrumentos más eficientes para construir patrimonio
              en el largo plazo. Diversificación profesional, costos bajos y
              acceso global — la base de cualquier portafolio moderno.
            </p>
          </div>
        </div>

        {/* Toggle pills */}
        <div className="flex flex-wrap gap-3 mb-10" data-testid="instruments-toggle">
          {Object.values(PRODUCTS).map((p) => (
            <button
              key={p.key}
              data-testid={`toggle-${p.key}`}
              onClick={() => setActive(p.key)}
              className={`px-6 py-3 rounded-sm text-sm tracking-wider uppercase font-medium transition-all duration-300 ${
                active === p.key
                  ? "bg-[#3D1565] text-white"
                  : "border border-white/15 text-[#F9F6F0]/70 hover:border-[#E6D5B8] hover:text-[#E6D5B8]"
              }`}
            >
              {p.title}
            </button>
          ))}
        </div>

        {/* Content card */}
        <div
          key={active}
          className="grid lg:grid-cols-12 gap-10 lg:gap-16 bg-[#150A1F]/60 border border-white/10 backdrop-blur-sm p-8 lg:p-14 reveal"
          data-testid="instruments-card"
        >
          {/* Left: text */}
          <div className="lg:col-span-7">
            <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-4">
              {product.tagline}
            </div>
            <h3 className="font-display text-3xl lg:text-4xl font-light leading-tight mb-6">
              {product.title}
            </h3>
            <p className="text-base text-[#F9F6F0]/75 leading-relaxed font-light mb-8">
              {product.description}
            </p>

            <ul className="space-y-4 mb-8">
              {product.keyPoints.map((point, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm lg:text-base text-[#F9F6F0]/80 font-light"
                >
                  <ChevronRight
                    size={16}
                    className="text-[#E6D5B8] mt-1 shrink-0"
                  />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <div className="border-l-2 border-[#E6D5B8] pl-5 py-2">
              <p className="text-sm text-[#F9F6F0]/65 italic font-light">
                {product.example}
              </p>
            </div>
          </div>

          {/* Right: visualization */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Diversification grid */}
            <div className="bg-[#0B0410]/60 border border-white/10 p-6">
              <div className="flex items-center gap-2 mb-5">
                <Layers size={16} className="text-[#E6D5B8]" />
                <span className="text-[11px] tracking-luxury uppercase text-[#E6D5B8]">
                  Diversificación
                </span>
              </div>
              <div className="grid grid-cols-10 gap-1.5">
                {Array.from({ length: 50 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-sm"
                    style={{
                      background:
                        active === "index"
                          ? `rgba(230,213,184,${0.25 + (i % 5) * 0.15})`
                          : `rgba(61,21,101,${0.35 + (i % 4) * 0.15})`,
                      transition: `all 0.4s ease ${i * 0.012}s`,
                    }}
                  />
                ))}
              </div>
              <p className="text-xs text-[#F9F6F0]/55 mt-4 font-light">
                Cada celda representa una empresa del índice. Tu inversión se
                reparte entre todas ellas automáticamente.
              </p>
            </div>

            {/* 3 stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center bg-[#0B0410]/60 border border-white/10 p-5">
                {active === "index" ? (
                  <Award size={18} className="text-[#E6D5B8] mx-auto mb-2" />
                ) : (
                  <Globe size={18} className="text-[#E6D5B8] mx-auto mb-2" />
                )}
                <div className="font-display text-xl lg:text-2xl text-[#E6D5B8] whitespace-nowrap">
                  {active === "index" ? "+50 años" : "Global"}
                </div>
                <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mt-1 leading-tight">
                  {active === "index"
                    ? "Historia comprobada"
                    : "Cobertura mundial"}
                </div>
              </div>
              <div className="text-center bg-[#0B0410]/60 border border-white/10 p-5">
                <Shield size={18} className="text-[#E6D5B8] mx-auto mb-2" />
                <div className="font-display text-2xl text-[#E6D5B8]">
                  {active === "index" ? "500+" : "1000+"}
                </div>
                <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mt-1">
                  Empresas
                </div>
              </div>
              <div className="text-center bg-[#0B0410]/60 border border-white/10 p-5">
                <TrendingUp size={18} className="text-[#E6D5B8] mx-auto mb-2" />
                <div className="font-display text-2xl text-[#E6D5B8]">
                  {active === "index" ? "Diario" : "Intradía"}
                </div>
                <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mt-1">
                  Liquidez
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA banner */}
        <div className="mt-12">
          <ScheduleCTA
            eyebrow="¿Listo para invertir?"
            testId="indexed-funds-cta"
          />
        </div>
      </div>
    </section>
  );
};

export default IndexedFunds;
